import { __assign } from "tslib";
import { pickBy } from 'lodash-es';
import update from 'immutability-helper';
import { overlapsHeader, overlapsFooter } from '../utils/hotspot';
import * as actionTypes from '../constants/actionTypes';
import * as screenLocation from '../constants/screenLocation';
import { getCurrentScale } from '../selectors/layout';
export var initialState = {
    annotations: {},
    nextAnnotation: null,
    highlightAnnotation: null,
    pulseAnnotation: null,
    isAnnotationMoveable: {},
};
// The input of this reducer is the entire application state.
// we use reduceReducers because annotations need to know about the rendered
// screen size, header, footer, etc.
export default function annotationReducer(globalState, action) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    switch (action.type) {
        case actionTypes.ENTER_ANNOTATION_MODE: {
            var commentUUID = action.nextCommentUUID, reference = action.nextReference;
            var nextAnnotation = {
                commentUUID: commentUUID,
                reference: reference,
            };
            return updateAnnotation(globalState, {
                nextAnnotation: { $set: nextAnnotation },
            });
        }
        case actionTypes.LEAVE_ANNOTATION_MODE: {
            return updateAnnotation(globalState, {
                nextAnnotation: { $set: null },
                highlightAnnotation: { $set: null },
            });
        }
        case actionTypes.HIDE_COMMENTS:
        case actionTypes.CANCEL_ANNOTATION: {
            return removeUnsavedAnnotation(globalState);
        }
        case actionTypes.ADD_NEW_ANNOTATION: {
            var _j = globalState.annotation.nextAnnotation, commentUUID = _j.commentUUID, reference = _j.reference;
            var screenArea = action.screenArea, offset = action.offset;
            var renderingScale = getCurrentScale(globalState);
            var newPosition = updateAnnotationAnnotationPosition({
                scale: renderingScale,
                position: getAnnotationInitialPosition(screenArea, globalState.screen.currentScreen),
                offset: offset,
            });
            var annotation = __assign(__assign({}, newPosition), { screenArea: screenArea, ownerScreenID: globalState.screen.currentScreen.id, commentUUID: commentUUID,
                reference: reference });
            return updateAnnotation(globalState, {
                annotations: { $merge: (_a = {}, _a[commentUUID] = annotation, _a) },
                nextAnnotation: { $set: null },
            });
        }
        case actionTypes.POSITION_ANNOTATION: {
            var commentUUID = action.commentUUID, toScreenArea = action.toScreenArea, offset = action.offset;
            var renderingScale = getCurrentScale(globalState);
            var newPosition = updateAnnotationAnnotationPosition({
                scale: renderingScale,
                position: getAnnotationInitialPosition(toScreenArea, globalState.screen.currentScreen),
                offset: offset,
            });
            var annotation = __assign(__assign({}, newPosition), { screenArea: toScreenArea });
            return updateAnnotation(globalState, {
                annotations: (_b = {},
                    _b[commentUUID] = { $merge: annotation },
                    _b),
            });
        }
        case actionTypes.ADD_ANNOTATION: {
            var screenArea = void 0;
            if (globalState.screen.currentOverlayHotspot.id) {
                screenArea = screenLocation.OVERLAY;
            }
            else {
                screenArea = getAnnotationScreenArea(action.annotation, globalState.screen.currentScreen);
            }
            var annotation = __assign(__assign({}, action.annotation), { screenArea: screenArea });
            var commentUUID = annotation.commentUUID;
            if (globalState.annotation.annotations[commentUUID]) {
                return updateAnnotation(globalState, {
                    annotations: (_c = {},
                        _c[commentUUID] = {
                            $set: __assign(__assign({}, annotation), { isSaved: !!annotation.commentID }),
                        },
                        _c),
                });
            }
            return updateAnnotation(globalState, {
                annotations: {
                    $merge: (_d = {},
                        _d[commentUUID] = __assign(__assign({}, annotation), { isSaved: true }),
                        _d),
                },
            });
        }
        case actionTypes.MARK_ANNOTATION_SAVED: {
            var commentUUID = action.commentUUID;
            return updateAnnotation(globalState, {
                annotations: (_e = {},
                    _e[commentUUID] = { isSaved: { $set: true } },
                    _e),
            });
        }
        case actionTypes.ANNOTATION_COMMENT_UPDATED: {
            var commentUUID = action.commentUUID, isDone = action.isDone;
            return updateAnnotation(globalState, {
                annotations: (_f = {},
                    _f[commentUUID] = { isDone: { $set: isDone } },
                    _f),
            });
        }
        case actionTypes.UPDATE_ANNOTATION: {
            var commentUUID = action.commentUUID, annotation = action.annotation;
            return updateAnnotation(globalState, {
                annotations: (_g = {},
                    _g[commentUUID] = { $merge: annotation },
                    _g),
            });
        }
        case actionTypes.REMOVE_ANNOTATION: {
            var commentUUID_1 = action.commentUUID;
            /* eslint-disable eqeqeq */
            var annotationsAfterRemoval = pickBy(globalState.annotation.annotations, function (_, key) { return key != commentUUID_1; });
            /* eslint-enable eqeqeq */
            return updateAnnotation(globalState, {
                annotations: { $set: annotationsAfterRemoval },
                highlightAnnotation: { $set: null },
            });
        }
        case actionTypes.SCROLL_TO_ANNOTATION: {
            var commentUUID = action.commentUUID;
            var annotation = globalState.annotation.annotations[commentUUID];
            if (!annotation) {
                return globalState;
            }
            var scrollTo_1 = annotation.y;
            return update(globalState, {
                screen: {
                    scrollTo: { $set: scrollTo_1 },
                    scrollingToAnnotation: { $set: commentUUID },
                },
            });
        }
        case actionTypes.HIGHLIGHT_ANNOTATION: {
            var commentUUID = action.commentUUID;
            return updateAnnotation(globalState, {
                highlightAnnotation: { $set: commentUUID },
            });
        }
        case actionTypes.UNHIGHLIGHT_ANNOTATION: {
            return updateAnnotation(globalState, {
                highlightAnnotation: { $set: null },
            });
        }
        case actionTypes.PULSE_ANNOTATION: {
            return update(globalState, {
                screen: {
                    scrollingToAnnotation: { $set: null },
                },
            });
        }
        case actionTypes.MAKE_ANNOTATION_MOVEABLE: {
            var commentUUID = action.commentUUID;
            return updateAnnotation(globalState, {
                isAnnotationMoveable: { $merge: (_h = {}, _h[commentUUID] = true, _h) },
            });
        }
        case actionTypes.CLEAR_ANNOTATIONS: {
            return updateAnnotation(globalState, {
                annotations: {
                    $set: {},
                },
                highlightAnnotation: { $set: null },
                isAnnotationMoveable: { $set: {} },
            });
        }
        default:
            return globalState;
    }
}
function updateAnnotation(globalState, specFragment) {
    return update(globalState, { annotation: specFragment });
}
function removeUnsavedAnnotation(globalState) {
    var saveAnnotations = pickBy(globalState.annotation.annotations, function (annotation) { return annotation.isSaved; });
    return updateAnnotation(globalState, {
        annotations: { $set: saveAnnotations },
    });
}
function getAnnotationInitialPosition(type, screen) {
    switch (type) {
        case screenLocation.HEADER:
            return getHeaderAnnotationInitialPosition();
        case screenLocation.BODY:
            return getBodyAnnotationInitialPosition(screen);
        case screenLocation.FOOTER:
            return getFooterAnnotationInitialPosition(screen);
        case screenLocation.OVERLAY:
            return getOverlayAnnotationInitialPosition();
        default:
            throw new Error("'" + type + "' is not a valid annotation type");
    }
}
function getAnnotationScreenArea(_a, screen) {
    var y = _a.y;
    if (overlapsHeader(screen.header, { y: y })) {
        return screenLocation.HEADER;
    }
    if (overlapsFooter(screen.footer, { y: y })) {
        return screenLocation.FOOTER;
    }
    return screenLocation.BODY;
    // TODO: handle screenLocation.OVERLAY?
}
function updateAnnotationAnnotationPosition(_a) {
    var scale = _a.scale, position = _a.position, offset = _a.offset;
    // TODO remove this hack to get actual scale value
    var actualScale = scale(100000) / 100000;
    // ////////////////////////////////////////////////
    var xOffset = offset.x / actualScale;
    var yOffset = offset.y / actualScale;
    return {
        x: position.x + xOffset,
        y: position.y + yOffset,
    };
}
function getHeaderAnnotationInitialPosition() {
    return { x: 0, y: 0 };
}
function getBodyAnnotationInitialPosition(screen) {
    return {
        x: 0,
        y: screen.header ? screen.header.height : 0,
    };
}
function getFooterAnnotationInitialPosition(screen) {
    return {
        x: 0,
        y: screen.img.height - screen.footer.height,
    };
}
function getOverlayAnnotationInitialPosition() {
    return {
        x: 0,
        y: 0,
    };
}
